import type { ActivableProductInformationId, ActivableProductInformationIdTag } from '@orus.eu/dimensions'
import { type RcphVersion } from '@orus.eu/dimensions-rcph'
import type { Product, ProductFamily } from '@orus.eu/product'
import type { ParameterlessTranslationKey, TranslateInSpecificLanguageFunction } from '@orus.eu/translations'

import type { BadgeProps } from '../../../../components/index.ts'

export type Tag =
  | ActivableProductInformationIdTag
  | 'MRP'
  | 'FR-RCP'
  | 'ES-MRPW'
  | 'NL-RCPH'
  | 'NL-AOVQL'
  | 'FR-RCPW'
  | 'FR-RCCW'
  | 'FR-AUPA'
  | 'FR-MRGA'
  | 'FR-MRPAXE'
  | 'FR-RCPAXE'

export type BadgeUserType = 'partner' | 'platform' | 'client'

export type BadgeLabelAndVariantProps = {
  props: { variant: BadgeProps['variant']; dark?: BadgeProps['dark'] | undefined }
  label: Record<BadgeUserType, string> | string
  tooltipKey?:
    | {
        partner: ParameterlessTranslationKey
        platform: ParameterlessTranslationKey
      }
    | ParameterlessTranslationKey
    | undefined
}

export const tagPropsPerTag: Record<Tag, BadgeLabelAndVariantProps> = {
  MRPH: {
    label: { partner: 'MRP', platform: 'MRPH', client: 'MRP' },
    props: { variant: 'jasmine' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_mrph_platform',
      partner: 'backoffice_product_tag_tooltip_mrph_partner',
    },
  },
  MRPW: {
    label: { partner: 'MRP', platform: 'MRPW', client: 'MRP' },
    props: { variant: 'jasmine' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_mrpw_platform',
      partner: 'backoffice_product_tag_tooltip_mrpw_partner',
    },
  },
  RCPH: {
    label: { partner: 'RCP', platform: 'RCPH', client: 'RCP' },
    props: { variant: 'sky' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_rcph_platform',
      partner: 'backoffice_product_tag_tooltip_rcph_partner',
    },
  },
  CYBER: {
    label: 'CYBER',
    props: { variant: 'mindaro' },
    tooltipKey: 'backoffice_product_tag_tooltip_cyber',
  },
  MUTA: {
    label: { partner: 'MUT', platform: 'MUTA', client: 'MUT' },
    props: { variant: 'peach' },
    tooltipKey: 'backoffice_product_tag_tooltip_muta',
  },
  RCDA: {
    label: { partner: 'RCD', platform: 'RCDA', client: 'RCD' },
    props: { variant: 'periwinkle' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_rcda_platform',
      partner: 'backoffice_product_tag_tooltip_rcda_partner',
    },
  },
  TDMI: {
    label: 'TDMI',
    props: { variant: 'periwinkle' },
    tooltipKey: 'backoffice_product_tag_tooltip_tdmi',
  },
  TRM: {
    label: 'TRM',
    props: { variant: 'periwinkle' },
    tooltipKey: 'backoffice_product_tag_tooltip_trm',
  },
  'MRP-SMALL-OFFICE': {
    label: 'SMALL OFFICE',
    props: { variant: 'mindaro' },
    tooltipKey: 'backoffice_product_tag_tooltip_mrp_small_office',
  },
  'ES-RCPH': {
    label: { partner: '🇪🇸 RCP', platform: '🇪🇸 RCPH', client: '🇪🇸 RCP' },
    props: { variant: 'sky' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_es_rcph_platform',
      partner: 'backoffice_product_tag_tooltip_es_rcph_partner',
    },
  },
  'FR-PREL': {
    label: { partner: 'PREV', platform: 'PREL', client: 'PREV' },
    props: { variant: 'peach' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_prel_platform',
      partner: 'backoffice_product_tag_tooltip_fr_prel_partner',
    },
  },
  RCPA: {
    label: { partner: 'RCP', platform: 'RCPA', client: 'RCP' },
    props: { variant: 'mindaro' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_rcpa_platform',
      partner: 'backoffice_product_tag_tooltip_rcpa_partner',
    },
  },
  MRPA: {
    label: { partner: 'MRP', platform: 'MRPA', client: 'MRP' },
    props: { variant: 'jasmine' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_mrpa_platform',
      partner: 'backoffice_product_tag_tooltip_mrpa_partner',
    },
  },
  RCPAXA: {
    label: { partner: 'RCP', platform: 'RCPAXA', client: 'RCP' },
    props: { variant: 'sky' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_rcpaxa_platform',
      partner: 'backoffice_product_tag_tooltip_rcpaxa_partner',
    },
  },
  MRP: {
    label: { partner: 'MRP', platform: 'MRP', client: 'MRP' },
    props: { variant: 'jasmine' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_mrp_platform',
      partner: 'backoffice_product_tag_tooltip_mrp_partner',
    },
  },
  'ES-MRPW': {
    label: { partner: '🇪🇸 MRP', platform: '🇪🇸 MRPW', client: '🇪🇸 MRP' },
    props: { variant: 'jasmine' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_es_mrpw_platform',
      partner: 'backoffice_product_tag_tooltip_es_mrpw_partner',
    },
  },
  'NL-RCPH': {
    label: { partner: '🇳🇱 RCP', platform: '🇳🇱 RCPH', client: '🇳🇱 RCP' },
    props: { variant: 'sky' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_nl_rcph_platform',
      partner: 'backoffice_product_tag_tooltip_nl_rcph_partner',
    },
  },
  // TODO (NL-AOVQL): check before MEP - label, variant and tooltip keys still need product-team review.
  'NL-AOVQL': {
    label: { partner: '🇳🇱 AOV', platform: '🇳🇱 AOVQL', client: '🇳🇱 AOV' },
    props: { variant: 'peach' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_nl_aovql_platform',
      partner: 'backoffice_product_tag_tooltip_nl_aovql_partner',
    },
  },
  'FR-RCPW': {
    label: { partner: 'RCP', platform: 'RCPW', client: 'RCP' },
    props: { variant: 'sky' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_rcpw_platform',
      partner: 'backoffice_product_tag_tooltip_fr_rcpw_partner',
    },
  },
  'FR-RCCW': {
    label: { partner: 'VTC', platform: 'RCCW', client: 'VTC' },
    props: { variant: 'mindaro' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_rccw_platform',
      partner: 'backoffice_product_tag_tooltip_fr_rccw_partner',
    },
  },
  // TODO (FR-AUPA): check before MEP - label and tooltip keys still need product-team review.
  'FR-AUPA': {
    label: { partner: 'AUP', platform: 'AUPA', client: 'AUP' },
    props: { variant: 'mindaro' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_aupa_platform',
      partner: 'backoffice_product_tag_tooltip_fr_aupa_partner',
    },
  },
  // TODO (FR-MRGA): check before MEP - label, variant and tooltip keys still need product-team review.
  'FR-MRGA': {
    label: { partner: 'MRP', platform: 'MRGA', client: 'MRP' },
    props: { variant: 'jasmine' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_mrga_platform',
      partner: 'backoffice_product_tag_tooltip_fr_mrga_partner',
    },
  },
  'FR-MRPAXE': {
    label: { partner: 'MRP', platform: 'MRPAXE', client: 'MRP' },
    props: { variant: 'jasmine' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_mrpaxe_platform',
      partner: 'backoffice_product_tag_tooltip_fr_mrpaxe_partner',
    },
  },
  'FR-RCPAXE': {
    label: { partner: 'RCP', platform: 'RCPAXE', client: 'RCP' },
    props: { variant: 'sky' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_rcpaxe_platform',
      partner: 'backoffice_product_tag_tooltip_fr_rcpaxe_partner',
    },
  },
  'FR-RCP': {
    label: { partner: 'RCP', platform: 'RCP', client: 'RCP' },
    props: { variant: 'sky' },
    tooltipKey: {
      platform: 'backoffice_product_tag_tooltip_fr_rcp_platform',
      partner: 'backoffice_product_tag_tooltip_fr_rcp_partner',
    },
  },
}

export const badgePropsPerProduct: Record<Product, BadgeLabelAndVariantProps> = {
  mrph: tagPropsPerTag.MRPH,
  mrpw: tagPropsPerTag.MRPW,
  rcph: tagPropsPerTag.RCPH,
  muta: tagPropsPerTag.MUTA,
  rcda: tagPropsPerTag.RCDA,
  'es-rcph': tagPropsPerTag['ES-RCPH'],
  'fr-prel': tagPropsPerTag['FR-PREL'],
  'nl-aovql': tagPropsPerTag['NL-AOVQL'],
  'nl-rcph': tagPropsPerTag['NL-RCPH'],
  'fr-rcpw': tagPropsPerTag['FR-RCPW'],
  'fr-rccw': tagPropsPerTag['FR-RCCW'],
  'fr-aupa': tagPropsPerTag['FR-AUPA'],
  'fr-mrga': tagPropsPerTag['FR-MRGA'],
  'fr-mrpaxe': tagPropsPerTag['FR-MRPAXE'],
  'fr-rcpaxe': tagPropsPerTag['FR-RCPAXE'],
  rcpa: tagPropsPerTag.RCPA,
  mrpa: tagPropsPerTag.MRPA,
  rcpaxa: tagPropsPerTag.RCPAXA,
  'es-mrpw': tagPropsPerTag['ES-MRPW'],
}

export const badgePropsPerProductFamily: Record<ProductFamily, BadgeLabelAndVariantProps> = {
  'fr-rcp': tagPropsPerTag['FR-RCP'],
  mrp: tagPropsPerTag.MRP,
}

export const productVersionTag = (product: Product, rcphVersion: RcphVersion | null | undefined): string => {
  switch (product) {
    case 'mrph':
    case 'mrpw':
    case 'muta':
    case 'rcda':
    case 'es-rcph':
    case 'fr-prel':
    case 'nl-aovql':
    case 'nl-rcph':
    case 'fr-rcpw':
    case 'fr-rccw':
    case 'fr-aupa':
    case 'fr-mrga':
    case 'fr-mrpaxe':
    case 'fr-rcpaxe':
    case 'rcpa':
    case 'rcpaxa':
    case 'es-mrpw':
      return ''
    case 'rcph':
      return rcphVersion ? `( version ${rcphVersion})` : ''
    case 'mrpa':
      return ''
  }
}

export const badgePropsPerActivableProductInformationId: Record<
  Product | ActivableProductInformationId,
  BadgeLabelAndVariantProps
> = {
  ...badgePropsPerProduct,
  'rc-pro': tagPropsPerTag.RCPH,
  'rcph-cyber': tagPropsPerTag.CYBER,
  'rcph-tdmi': tagPropsPerTag.TDMI,
  'mrp-small-office': tagPropsPerTag['MRP-SMALL-OFFICE'],
  'es-rcph-pro': tagPropsPerTag['ES-RCPH'],
  'es-rcph-general': tagPropsPerTag['ES-RCPH'],
  rcpa: tagPropsPerTag.RCPA,
}

export function getBadgeLabel({ label }: BadgeLabelAndVariantProps, userType: BadgeUserType): string {
  return typeof label === 'string' ? label : label[userType]
}

/**
 * @returns undefined for a product ID that a newer backend introduced while the tab still runs an older release
 */
function findBadgeProps(product: Product | ActivableProductInformationId): BadgeLabelAndVariantProps | undefined {
  return badgePropsPerActivableProductInformationId[product]
}

export function getBadgeName(product: Product | ActivableProductInformationId, userType: BadgeUserType): string {
  const props = findBadgeProps(product)
  return props ? getBadgeLabel(props, userType) : product
}

export function getProductDisplayName(
  product: Product | ActivableProductInformationId,
  userType: BadgeUserType,
  translate: TranslateInSpecificLanguageFunction,
): string {
  const props = findBadgeProps(product)
  if (!props) {
    return product
  }
  if (typeof props.tooltipKey === 'string') {
    return translate(props.tooltipKey)
  }
  if (userType === 'partner') {
    return props.tooltipKey ? translate(props.tooltipKey.partner) : getBadgeLabel(props, 'partner')
  }
  if (userType === 'platform') {
    return props.tooltipKey ? translate(props.tooltipKey.platform) : getBadgeLabel(props, 'platform')
  }

  return getBadgeLabel(props, userType)
}
